<template>
  <div class="kpi-templates-management">
    <NavigationBar/>
    <div class="layout-content">
      <div class="page-wrapper">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1 class="page-title">KPI Templates (by Position)</h1>
            <div class="breadcrumb">
              <a class="breadcrumb-link" @click="gotTo('employee-register')">Employees</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">KPI Templates</span>
            </div>
          </div>
        </div>

        <!-- Main Card -->
        <div class="main-card">
          <!-- Card Header with Filters -->
          <div class="card-header">
            <div class="header-filters">
              <div class="filter-group">
                <input 
                  v-model="filterPosition" 
                  type="text" 
                  class="filter-input" 
                  placeholder="Filter by position"
                >
                <button class="btn btn-primary btn-small" @click="loadTemplates">Apply</button>
              </div>
              <button class="btn btn-success btn-small" @click="openModal()">
                <span class="btn-icon">+</span> Add KPI Template
              </button>
            </div>
          </div>

          <!-- Card Body -->
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p class="loading-text">Loading...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="templates.length === 0" class="empty-state">
              <div class="empty-icon">📊</div>
              <p class="empty-text">No KPI templates yet. Add templates for each position; they will be auto-attached when employees are onboarded.</p>
            </div>

            <!-- Templates Table -->
            <div v-else class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>KPI Name</th>
                    <th>Description</th>
                    <th>Weight %</th>
                    <th>Target Type</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in templates" :key="t.id" class="table-row">
                    <td><strong>{{ t.position_designation }}</strong></td>
                    <td>{{ t.kpi_name }}</td>
                    <td>
                      <span class="description-text">
                        {{ (t.description || '').slice(0, 60) }}{{ (t.description || '').length > 60 ? '...' : '' }}
                      </span>
                    </td>
                    <td>{{ t.weight }}</td>
                    <td>
                      <span class="target-type-badge">
                        {{ t.target_type || 'numeric' }}
                      </span>
                    </td>
                    <td class="text-right">
                      <button class="btn btn-outline btn-small me-1" @click="openModal(t)">Edit</button>
                      <button class="btn btn-danger btn-small" @click="deleteTemplate(t)">Delete</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Modal -->
        <div v-if="showModal" class="modal-overlay" @click="closeModal">
          <div class="modal-container" @click.stop>
            <div class="modal-header">
              <h3 class="modal-title">{{ form.id ? 'Edit' : 'Add' }} KPI Template</h3>
              <button class="modal-close" @click="closeModal">&times;</button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label">Position / Designation <span class="required">*</span></label>
                <input 
                  v-model="form.position_designation" 
                  type="text" 
                  class="form-input" 
                  list="kpi-position-options"
                  placeholder="e.g. Software Engineer, HR Manager"
                >
                <datalist id="kpi-position-options">
                  <option v-for="p in positionOptions" :key="p" :value="p" />
                </datalist>
              </div>
              <div class="form-group">
                <label class="form-label">KPI Name <span class="required">*</span></label>
                <input 
                  v-model="form.kpi_name" 
                  type="text" 
                  class="form-input" 
                  placeholder="e.g. Project Delivery"
                >
              </div>
              <div class="form-group">
                <label class="form-label">Description</label>
                <textarea 
                  v-model="form.description" 
                  class="form-textarea" 
                  rows="2"
                  placeholder="Describe the KPI"
                ></textarea>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label">Weight %</label>
                  <input 
                    v-model.number="form.weight" 
                    type="number" 
                    class="form-input" 
                    min="0" 
                    max="100" 
                    step="0.01"
                    placeholder="0-100"
                  >
                </div>
                <div class="form-group">
                  <label class="form-label">Target Type</label>
                  <select v-model="form.target_type" class="form-select">
                    <option value="numeric">Numeric</option>
                    <option value="percentage">Percentage</option>
                    <option value="boolean">Boolean</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">Display Order</label>
                  <input 
                    v-model.number="form.display_order" 
                    type="number" 
                    class="form-input" 
                    min="0"
                    placeholder="Order"
                  >
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Target Value / Measurement Unit</label>
                <input 
                  v-model="form.target_value" 
                  type="text" 
                  class="form-input" 
                  placeholder="e.g. 10, 95%, Yes"
                >
              </div>
              <div class="form-group">
                <label class="form-label">Measurement Unit</label>
                <input 
                  v-model="form.measurement_unit" 
                  type="text" 
                  class="form-input" 
                  placeholder="e.g. %, count, days"
                >
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button class="btn btn-primary" @click="saveTemplate" :disabled="saving">
                <span v-if="saving" class="btn-spinner"></span>
                {{ saving ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "KpiTemplatesManagement",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      saving: false,
      showModal: false,
      filterPosition: '',
      positionOptions: [],
      templates: [],
      form: {
        id: null,
        position_designation: '',
        kpi_name: '',
        description: '',
        weight: 0,
        target_type: 'numeric',
        target_value: '',
        measurement_unit: '',
        display_order: 0
      }
    };
  },
  mounted() {
    this.loadPositionOptions();
    this.loadTemplates();
  },
  methods: {
    async loadPositionOptions() {
      try {
        const data = new FormData();
        data.append("function", "getDistinctPositionsForKpi");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.positionOptions = res.data.data?.positions ?? res.data.positions ?? [];
        }
      } catch (e) {
        console.error("Failed to load positions:", e);
      }
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getKpiTemplates");
        if (this.filterPosition) data.append("position", this.filterPosition);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.templates = res.data.data?.templates ?? res.data.templates ?? [];
        } else {
          this.templates = [];
        }
      } catch (e) {
        console.error("Failed to load KPI templates:", e);
        this.templates = [];
      } finally {
        this.loading = false;
      }
    },
    openModal(t = null) {
      this.form = t ? { ...t } : {
        id: null, 
        position_designation: '', 
        kpi_name: '', 
        description: '', 
        weight: 0,
        target_type: 'numeric', 
        target_value: '', 
        measurement_unit: '', 
        display_order: 0
      };
      this.showModal = true;
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.showModal = false;
      document.body.style.overflow = '';
    },
    async saveTemplate() {
      if (!this.form.position_designation || !this.form.kpi_name) {
        this.$toast?.error("Position and KPI name are required") || alert("Position and KPI name are required");
        return;
      }
      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "saveKpiTemplate");
        if (this.form.id) data.append("id", this.form.id);
        data.append("position_designation", this.form.position_designation);
        data.append("kpi_name", this.form.kpi_name);
        data.append("description", this.form.description || '');
        data.append("weight", this.form.weight);
        data.append("target_type", this.form.target_type);
        data.append("target_value", this.form.target_value || '');
        data.append("measurement_unit", this.form.measurement_unit || '');
        data.append("display_order", this.form.display_order);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("KPI template saved") || alert("Saved");
          this.closeModal();
          this.loadTemplates();
        } else {
          throw new Error(res?.data?.message || "Failed to save");
        }
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      } finally {
        this.saving = false;
      }
    },
    async deleteTemplate(t) {
      if (!confirm("Delete this KPI template?")) return;
      try {
        const data = new FormData();
        data.append("function", "deleteKpiTemplate");
        data.append("id", t.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("Deleted") || alert("Deleted");
          this.loadTemplates();
        } else {
          throw new Error(res?.data?.message || "Failed");
        }
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      }
    },
    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
/* Layout */
.kpi-templates-management {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.layout-content {
  padding: 20px;
}

.page-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
}

.breadcrumb-link {
  color: #0066cc;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  font-size: 14px;
  padding: 0;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: #999;
}

.breadcrumb-current {
  color: #333;
  font-weight: 500;
}

/* Main Card */
.main-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
}

.card-header {
  padding: 16px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.header-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  width: 200px;
  max-width: 100%;
}

.filter-input:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
  border-color: #0066cc;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0052a3;
}

.btn-success {
  background-color: #00a854;
  color: white;
  border-color: #00a854;
}

.btn-success:hover:not(:disabled) {
  background-color: #008f46;
}

.btn-secondary {
  background-color: #cd1426;
  color: #333;
  border-color: #ddd;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.btn-outline {
  background-color: transparent;
  color: #0066cc;
  border-color: #0066cc;
}

.btn-outline:hover:not(:disabled) {
  background-color: rgba(0, 102, 204, 0.1);
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-icon {
  font-size: 16px;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  margin-right: 6px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card Body */
.card-body {
  padding: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 102, 204, 0.1);
  border-top-color: #0066cc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 12px;
  color: #666;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #999;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  margin: 0;
  max-width: 600px;
  line-height: 1.5;
}

/* Table */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  color: #333;
  vertical-align: middle;
}

.data-table tbody tr {
  transition: background-color 0.2s;
}

.data-table tbody tr:hover {
  background-color: rgba(0, 102, 204, 0.05);
}

.text-right {
  text-align: right;
}

.description-text {
  color: #666;
  line-height: 1.4;
}

.target-type-badge {
  display: inline-block;
  padding: 3px 8px;
  background-color: #e9ecef;
  color: #495057;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;
}

.modal-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  width: 600px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Form Styles */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
  background-color: white;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

.form-textarea {
  min-height: 60px;
  resize: vertical;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .layout-content {
    padding: 12px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .filter-input {
    width: 100%;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 12px;
  }
  
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-container {
    max-height: 95vh;
  }
  
  .modal-body {
    padding: 16px;
  }
  
  .text-right {
    text-align: left;
  }
  
  .data-table td.text-right {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }
  
  .data-table {
    font-size: 13px;
  }
  
  .btn-small {
    padding: 4px 8px;
    font-size: 12px;
  }
}
</style>